// watch/VideoMoreMenu.tsx — the watch page's ⋯ action menu (1.27.50, owner concept
// video_watch_page_v2). Grouped like the concept:
//
//   VIDEO            Add to queue › · Video details · Download video › (when the uploader
//                    allows it, or it's yours) · Embed video (when allowed) · Mark as
//                    watched / unwatched (signed in)
//   RECOMMENDATIONS  Not interested · Don't recommend this creator · Mute @handle
//   REPORT           Report video · Report playback issue
//   YOUR VIDEO       Video settings (who can watch, permissions, captions) · Edit post ·
//                    Delete post   (own video only)
//
// Show transcript appears once a video has captions. "Not interested" hides the video (the same hide as
// everywhere) and moves you on — this page can't keep showing a post you just hid.
import { useState, type ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Dialog, IconButton, MetaRow, Popover, useToast } from '@/ui';
import { Icon } from '@/icons';
import { useMute, useRequireAuth, useSession } from '@/hooks';
import { useWriteGate } from '@/hooks/writeGate';
import { usePostStore } from '@/core/stores/postStore';
import { postsService } from '@/core/services/postsService';
import { videoFeedService } from '@/core/services/videoFeedService';
import type { Post } from '@/core/models/post';
import { formatCount } from '@/components/formatCount';
import { formatClock } from '../../player/chapters';
import { renditions } from '../../player/playerPrefs';
import { MenuItem, MenuPanel } from './actionStyles';
import { VideoSettingsDialog } from './VideoSettingsDialog';
import { useVideoQueue } from '../../queue/videoQueue';
import { EmbedDialog } from './EmbedDialog';

const Details = styled.dl`
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 8px 16px;
  margin: 0;
  font: 500 ${({ theme }) => theme.fontSize.sm} ${({ theme }) => theme.font.body};
  dt {
    color: ${({ theme }) => theme.color.textMuted};
  }
  dd {
    margin: 0;
    color: ${({ theme }) => theme.color.textPrimary};
    font-variant-numeric: tabular-nums;
  }
`;

const CATEGORY_LABEL: Record<string, string> = {
  gaming: 'Gaming',
  news: 'News',
  education: 'Education',
  music: 'Music',
  tech: 'Tech',
  sports: 'Sports',
};

export interface VideoMoreMenuProps {
  post: Post;
  title: string;
  isOwn: boolean;
  /** Where "Not interested" moves you (the next Up next video, else the Videos page). */
  nextHref: string;
  /** The player's current time, for "Report playback issue". */
  currentTime: () => number;
  onReport: () => void;
  onEdit: () => void;
  onDelete: () => void;
  /** A different trigger (Flash's round "More" button). */
  trigger?: ReactElement;
  /** Set when the video has captions: opens the page's Transcript tab. */
  onShowTranscript?: () => void;
}

export function VideoMoreMenu({
  post,
  title,
  isOwn,
  nextHref,
  currentTime,
  onReport,
  onEdit,
  onDelete,
  trigger,
  onShowTranscript,
}: VideoMoreMenuProps) {
  const requireAuth = useRequireAuth();
  const gate = useWriteGate();
  const toast = useToast();
  const navigate = useNavigate();
  const { isMuted, toggleMute } = useMute(post.handle);
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<'main' | 'queue' | 'download'>('main');
  const [embedAt, setEmbedAt] = useState<number | null>(null);
  const playNext = useVideoQueue((s) => s.playNext);
  const addToEnd = useVideoQueue((s) => s.addToEnd);
  const [details, setDetails] = useState(false);
  const [settings, setSettings] = useState(false);
  const { isAuthenticated } = useSession();
  const watched = post.watchProgress?.watched === true;
  const video = post.video!;

  function close() {
    setOpen(false);
    setView('main');
  }

  function queue(where: 'next' | 'end') {
    close();
    if (where === 'next') playNext(post);
    else addToEnd(post);
    toast(where === 'next' ? 'Plays next' : 'Added to the end of your queue');
  }

  function notInterested() {
    close();
    if (!requireAuth('hide this video')) return;
    void postsService.hidePost(post.id).then((ok) => {
      if (!ok) {
        toast('Something went wrong');
        return;
      }
      toast("You won't see this video again", {
        label: 'Undo',
        onClick: () => {
          void postsService.unhidePost(post).then((hidden) => {
            if (hidden === false) navigate(`/videos/${post.id}`);
            else toast('Something went wrong');
          });
        },
      });
      navigate(nextHref);
    });
  }

  function toggleWatched() {
    close();
    if (!requireAuth('keep track of what you have watched')) return;
    const patch = (watchProgress: Post['watchProgress']) =>
      usePostStore.getState().patchPost(post.id, { watchProgress });
    const done = watched
      ? videoFeedService.clearProgress(video.id).then(() => {
          patch(null);
          toast('Marked as unwatched');
        })
      : videoFeedService.saveProgress(video.id, video.durationMs, true).then((p) => {
          patch(p);
          toast('Marked as watched');
        });
    done.catch(() => toast('Something went wrong'));
  }

  function download(quality: 'original' | 'standard') {
    close();
    if (!requireAuth('download videos')) return;
    // A plain navigation: the server answers with a short-lived "save this file" link, so
    // the browser downloads and this page stays where it is.
    window.location.assign(
      `/api/videos/${video.id}/download/${quality === 'standard' ? '?quality=standard' : ''}`,
    );
  }

  function dontRecommend() {
    close();
    if (!requireAuth('change your recommendations')) return;
    void videoFeedService
      .hideCreator(post.handle)
      .then(() =>
        toast(`Videos from ${post.handle} won't be recommended to you`, {
          label: 'Undo',
          onClick: () => void videoFeedService.unhideCreator(post.handle).catch(() => {}),
        }),
      )
      .catch(() => toast('Something went wrong'));
  }

  function mute() {
    close();
    if (!requireAuth('mute')) return;
    void toggleMute().then((muted) => {
      if (muted === null) toast('Something went wrong');
      else toast(muted ? `Muted ${post.handle}` : `Unmuted ${post.handle}`);
    });
  }

  function reportPlayback() {
    close();
    const at = formatClock(currentTime());
    const body = [
      `Video: ${window.location.origin}/videos/${post.id}`,
      `Title: ${title}`,
      `At: ${at} of ${formatClock(video.durationMs / 1000)}`,
      `Video size: ${video.width} × ${video.height}`,
      `Browser: ${navigator.userAgent}`,
      '',
      'What went wrong:',
    ].join('\n');
    const q = new URLSearchParams({ submit: 'bug', title: `Playback issue: ${title}`, body });
    navigate(`/feedback?${q.toString()}`);
  }

  const { hdLabel, standardLabel } = renditions(video);
  const hasLight = video.url !== video.lightUrl;

  return (
    <>
      <Popover
        trigger={
          trigger ?? <IconButton icon="more-horizontal" label="More video actions" size={20} />
        }
        open={open}
        onOpenChange={(next) => (next ? setOpen(true) : close())}
        align="end"
      >
        {view === 'download' ? (
          <MenuPanel role="menu" aria-label="Download video">
            <MenuItem type="button" role="menuitem" onClick={() => setView('main')}>
              <Icon name="chevron-left" size={16} />
              <span>Back</span>
            </MenuItem>
            <hr />
            <MenuItem type="button" role="menuitem" onClick={() => download('original')}>
              <Icon name="download" size={16} />
              <span>Original ({hdLabel})</span>
            </MenuItem>
            {hasLight && (
              <MenuItem type="button" role="menuitem" onClick={() => download('standard')}>
                <Icon name="download" size={16} />
                <span>Smaller ({standardLabel})</span>
              </MenuItem>
            )}
          </MenuPanel>
        ) : view === 'queue' ? (
          <MenuPanel role="menu" aria-label="Add to queue">
            <MenuItem type="button" role="menuitem" onClick={() => setView('main')}>
              <Icon name="chevron-left" size={16} />
              <span>Back</span>
            </MenuItem>
            <hr />
            <MenuItem type="button" role="menuitem" onClick={() => queue('next')}>
              <Icon name="list-plus" size={16} />
              <span>Play next</span>
            </MenuItem>
            <MenuItem type="button" role="menuitem" onClick={() => queue('end')}>
              <Icon name="list" size={16} />
              <span>Add to end of queue</span>
            </MenuItem>
          </MenuPanel>
        ) : (
          <MenuPanel role="menu" aria-label="More video actions">
            <h3>Video</h3>
            <MenuItem type="button" role="menuitem" onClick={() => setView('queue')}>
              <Icon name="list-plus" size={16} />
              <span>Add to queue</span>
              <Icon name="chevron-right" size={14} />
            </MenuItem>
            {onShowTranscript && (
              <MenuItem type="button" role="menuitem" onClick={() => (close(), onShowTranscript())}>
                <Icon name="file-text" size={16} />
                <span>Show transcript</span>
              </MenuItem>
            )}
            <MenuItem type="button" role="menuitem" onClick={() => (close(), setDetails(true))}>
              <Icon name="info" size={16} />
              <span>Video details</span>
            </MenuItem>
            {(video.allowDownload || isOwn) && (
              <MenuItem type="button" role="menuitem" onClick={() => setView('download')}>
                <Icon name="download" size={16} />
                <span>Download video</span>
                <Icon name="chevron-right" size={14} />
              </MenuItem>
            )}
            {video.allowEmbed !== false && (
              <MenuItem
                type="button"
                role="menuitem"
                onClick={() => {
                  const at = currentTime();
                  close();
                  if (gate.allow('embed videos')) setEmbedAt(at);
                }}
              >
                <Icon name="code" size={16} />
                <span>Embed video</span>
              </MenuItem>
            )}
            {isAuthenticated && (
              <MenuItem type="button" role="menuitem" onClick={toggleWatched}>
                <Icon name={watched ? 'eye-off' : 'eye'} size={16} />
                <span>{watched ? 'Mark as unwatched' : 'Mark as watched'}</span>
              </MenuItem>
            )}
            {!isOwn && (
              <>
                <hr />
                <h3>Recommendations</h3>
                <MenuItem type="button" role="menuitem" onClick={notInterested}>
                  <Icon name="eye-off" size={16} />
                  <span>Not interested</span>
                </MenuItem>
                <MenuItem type="button" role="menuitem" onClick={dontRecommend}>
                  <Icon name="user-x" size={16} />
                  <span>Don’t recommend this creator</span>
                </MenuItem>
                <MenuItem type="button" role="menuitem" onClick={mute}>
                  <Icon name="volume-x" size={16} />
                  <span>
                    {isMuted ? 'Unmute' : 'Mute'} {post.handle}
                  </span>
                </MenuItem>
              </>
            )}
            <hr />
            <h3>Report</h3>
            {!isOwn && (
              <MenuItem
                type="button"
                role="menuitem"
                data-danger="true"
                onClick={() => (close(), onReport())}
              >
                <Icon name="flag" size={16} />
                <span>Report video</span>
              </MenuItem>
            )}
            <MenuItem type="button" role="menuitem" onClick={reportPlayback}>
              <Icon name="alert" size={16} />
              <span>Report playback issue</span>
            </MenuItem>
            {isOwn && (
              <>
                <hr />
                <h3>Your video</h3>
                <MenuItem
                  type="button"
                  role="menuitem"
                  onClick={() => (close(), setSettings(true))}
                >
                  <Icon name="lock" size={16} />
                  <span>Video settings</span>
                </MenuItem>
                <MenuItem type="button" role="menuitem" onClick={() => (close(), onEdit())}>
                  <Icon name="edit" size={16} />
                  <span>Edit post</span>
                </MenuItem>
                <MenuItem
                  type="button"
                  role="menuitem"
                  data-danger="true"
                  onClick={() => (close(), onDelete())}
                >
                  <Icon name="trash" size={16} />
                  <span>Delete post</span>
                </MenuItem>
              </>
            )}
          </MenuPanel>
        )}
      </Popover>

      {settings && <VideoSettingsDialog post={post} onClose={() => setSettings(false)} />}
      {embedAt !== null && (
        <EmbedDialog
          postId={post.id}
          title={title}
          startSec={embedAt}
          onClose={() => setEmbedAt(null)}
        />
      )}
      <Dialog open={details} onClose={() => setDetails(false)} title="Video details">
        <Details>
          <dt>Title</dt>
          <dd>{title}</dd>
          <dt>Posted</dt>
          <dd>
            {post.createdAt
              ? new Date(post.createdAt).toLocaleString(undefined, {
                  dateStyle: 'long',
                  timeStyle: 'short',
                })
              : post.time}
          </dd>
          <dt>Length</dt>
          <dd>{formatClock(video.durationMs / 1000)}</dd>
          <dt>Size</dt>
          <dd>
            {video.width} × {video.height}
            {video.height > video.width ? ' (portrait)' : ''}
          </dd>
          <dt>Quality</dt>
          <dd>{hasLight ? `${hdLabel}, with a ${standardLabel} copy for playback` : hdLabel}</dd>
          <dt>Category</dt>
          <dd>{video.category ? CATEGORY_LABEL[video.category] : 'None'}</dd>
          <dt>Type</dt>
          <dd>{video.isShort ? 'Flash (60 seconds or less)' : 'Video'}</dd>
          <dt>Views</dt>
          <dd>{formatCount(video.views ?? 0)}</dd>
          <dt>Reactions</dt>
          <dd>
            <MetaRow>
              <span>{formatCount(post.cheers)} cherishes</span>
              <span>{formatCount(post.dislikes ?? 0)} dislikes</span>
            </MetaRow>
          </dd>
        </Details>
      </Dialog>
    </>
  );
}
